import { useTranslation } from 'react-i18next'
import { SurfaceCard } from '../../design/SurfaceCard'
import type { MyAddon, MySubscription } from './types'

interface Props {
  subscription: MySubscription | null | undefined
  addons: MyAddon[]
}

/**
 * The plan in one line at the foot of the session, with its extras (sessions
 * with an instructor) under it. Inside the gym the plan is not what you came
 * for, but how many entries or trainer sessions are left still matters.
 */
export function PlanLine({ subscription, addons }: Props) {
  const { t } = useTranslation()
  if (!subscription && addons.length === 0) return null

  const detail = subscription
    ? subscription.expires_at
      ? t('membership.validUntil', {
          date: new Date(subscription.expires_at).toLocaleDateString(),
        })
      : subscription.entries_remaining !== null
        ? t('membership.entriesRemaining', { count: subscription.entries_remaining })
        : null
    : null

  return (
    <SurfaceCard className="flex flex-col gap-2 px-4 py-3">
      {/* The plan's full name, however long a gym makes it ("Abonament lunar cu
          instructor"), wrapping rather than cut off; what is left goes under it. */}
      {subscription && (
        <div className="min-w-0">
          <p className="break-words text-sm font-semibold leading-snug">
            {subscription.gym_plans.name}
          </p>
          {detail && <p className="mt-0.5 text-xs text-foreground/50">{detail}</p>}
        </div>
      )}
      {addons.length > 0 && (
        <div
          role="list"
          aria-label={t('membership.addonsLabel')}
          className="flex flex-wrap items-center gap-1.5"
        >
          <span className="eyebrow text-foreground/40">{t('membership.addonsLabel')}</span>
          {addons.map((addon) => (
            <span
              role="listitem"
              key={addon.id}
              className="rounded-full bg-[var(--gym-veil)] px-2.5 py-0.5 text-xs text-foreground/75"
            >
              {addon.gym_plans.name}
              {addon.entries_remaining !== null &&
                ` · ${t('membership.addonSessionsLeft', { count: addon.entries_remaining })}`}
            </span>
          ))}
        </div>
      )}
    </SurfaceCard>
  )
}
